import React, { useEffect, useState } from "react";
import axios from "axios";
import { Fade, Modal } from "@mui/material";
import { useSelector } from "react-redux";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import DButton from "../../../components/Dashboard/DashboardButtons/Dbutton";
import DButtonOutlined from "../../../components/Dashboard/DashboardButtons/dButtonOutlined";
import SearchBar from "../../../components/common/SearchBar/SearchBar";
import PaginationComponent from "../../../components/common/Pagination/PaginationComponent";
import DataTable from "../../../components/common/DataTable/DataTableSorting";
import DTextInput from "../../../components/Dashboard/DashboardInputs/DTextInput";
import DSelectInput from "../../../components/Dashboard/DashboardInputs/DSelectInput";
import DFileUpload from "../../../components/Dashboard/DashboardInputs/DFileUpload";
import DefaultLabel from "../../../components/Dashboard/DefaultLabel/DefaultLabel";
import SelectCountry from "../../../components/Dashboard/amount/SelectCountry";
import PopupFrame from "../../../components/Popups/PopupFrame/PopupFrame";
import SuccessPopupDashboard from "../../../components/Popups/PopupFrame/SuccessPopupDashboard";
import { API_ENDPOINT } from "../../../services/api/resources";
import styles from "./BudgetMaster.module.css";
import itemClasses from "../../OrganisationTabs/ItemsTab/ItemsTab.module.css";
import plusIcon from "../../../assets/images/svg/plusIcon.png";
import successPopupImg from "../../../assets/images/png/Group 2 2@2x.png";
import { previouPageClick } from "../../../utils/pagination";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

const financialYearLabel = (endYear) =>
  `${Number(endYear) - 1}-${String(endYear).slice(-2)}`;
const formatIndianDateTime = (dateValue) => {
  const date = new Date(dateValue);
  if (!dateValue || Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  }).format(date);
};
const years = Array.from({ length: 10 }, (_, index) =>
  financialYearLabel(new Date().getFullYear() + index + 1),
);
const emptyForm = {
  department: "",
  year: "",
  expenseType: "",
  purchaseType: "",
  amount: "",
};
const formatAllocatedAmount = (amount) => {
  const normalizedAmount = String(amount ?? "")
    .replace(/,/g, "")
    .trim();
  if (!normalizedAmount || !Number.isFinite(Number(normalizedAmount)))
    return "";
  const [wholeNumber, decimal] = normalizedAmount.split(".");
  const formattedWholeNumber = Number(wholeNumber).toLocaleString("en-IN");
  return decimal === undefined
    ? formattedWholeNumber
    : `${formattedWholeNumber}.${decimal}`;
};

const BudgetMaster = () => {
  const loginData = useSelector((state) => state.loginData.value);
  const authToken = loginData?.auth_token;
  const adminToken = loginData?.user?.user_token;
  const [departments, setDepartments] = useState([]);
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [currency, setCurrency] = useState({ currency: "", countryName: "" });
  const [errors, setErrors] = useState({});
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortingColumnName, setSortingColumnName] = useState("created_at");
  const [sortingOrder, setSortingOrder] = useState("desc");
  const [tableLoading, setTableLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [paginationData, setPaginationData] = useState({
    count: 0,
    page: 1,
    page_size: 25,
  });
  const [deleteSuccessOpen, setDeleteSuccessOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkError, setBulkError] = useState("");

  useEffect(() => {
    if (!adminToken) return;
    axios
      .post(
        API_ENDPOINT.get_Department,
        { user_token: adminToken },
        { headers: { Authorization: `Bearer ${authToken}` } },
      )
      .then((response) =>
        setDepartments(response?.data?.data || response?.data || []),
      )
      .catch(() => setDepartments([]));
  }, [adminToken, authToken]);

  const loadBudgets = async () => {
    if (!adminToken) return;
    try {
      setTableLoading(true);
      const response = await axios.post(
        API_ENDPOINT.get_budget_config,
        {
          admin_token: adminToken,
          search: searchQuery,
          page: pageNumber,
          page_size: 10,
          sort_by: sortingColumnName,
          order: sortingOrder,
        },
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      const data = response?.data || {};
      setRecords(data.results || []);
      setPaginationData({
        count: data.count || 0,
        page: data.page || pageNumber,
        page_size: data.page_size || 25,
      });
    } catch (error) {
      setRecords([]);
      setPaginationData({ count: 0, page: 1, page_size: 25 });
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    loadBudgets();
  }, [adminToken, authToken, pageNumber, searchQuery, sortingColumnName, sortingOrder]);

  const closeModal = () => {
    if (!saving) {
      setOpen(false);
      setErrors({});
    }
  };
  const openAddBudget = () => {
    setForm(emptyForm);
    setCurrency({ currency: "", countryName: "" });
    setErrors({});
    setEditingIndex(null);
    setOpen(true);
  };
  const openBulkBudget = () => {
    setBulkFile(null);
    setBulkError("");
    setErrors({});
    setBulkOpen(true);
  };
  const downloadBudgetTemplate = () => {
    const worksheet = XLSX.utils.aoa_to_sheet([
      [
        "Department",
        "Financial Year",
        "Expense Type",
        "Budget Category",
        "Currency",
        "Allocated Amount",
      ],
    ]);
    worksheet["!cols"] = [
      { wch: 25 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 12 },
      { wch: 20 },
      { wch: 12 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Budget Master");
    saveAs(
      new Blob([XLSX.write(workbook, { bookType: "xlsx", type: "array" })], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      "Budget Master Template.xlsx",
    );
  };
  const uploadBulkBudget = async () => {
    if (!bulkFile) return;
    const formData = new FormData();
    formData.append("budget_file", bulkFile);
    formData.append("admin_token", adminToken);
    try {
      setBulkSaving(true);
      setBulkError("");
      await axios.post(API_ENDPOINT.ins_budget_config, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      await loadBudgets();
      setBulkOpen(false);
      setBulkFile(null);
    } catch (error) {
      setBulkError(error?.response?.data?.response || error?.response?.data?.message || "Unable to upload budget file. Please try again.");
    } finally {
      setBulkSaving(false);
    }
  };
  const editBudget = (record, index) => {
    setForm({
      department: record.department,
      year: financialYearLabel(record.financial_year),
      expenseType: record.expense_type,
      purchaseType: record.purchase_type
        ? `${record.purchase_type.charAt(0).toUpperCase()}${record.purchase_type.slice(1).toLowerCase()}`
        : "",
      amount: String(record.allocated_amount ?? "").replace(/,/g, ""),
    });
    setCurrency({ currency: record.currency || "", countryName: "" });
    setErrors({});
    setEditingIndex(index);
    setOpen(true);
  };
  const saveBudget = async () => {
    const department = departments.find(
      (item) => item.name === form.department,
    );
    const amount = String(form.amount).replace(/,/g, "").trim();
    const validation = {
      department: !department,
      year: !form.year,
      expenseType: !form.expenseType,
      purchaseType: !form.purchaseType,
      currency: !currency.currency,
      amount: !/^\d+(\.\d{1,2})?$/.test(amount),
    };
    setErrors(validation);
    if (Object.values(validation).some(Boolean)) return;
    const payload = {
      admin_token: adminToken,
      ...(editingIndex !== null && {
        id: records[editingIndex]?.id,
        revision: records[editingIndex]?.revision,
      }),
      department_id: department.id,
      financial_year: form.year,
      expense_type: form.expenseType,
      purchase_type: form.purchaseType.toLowerCase(),
      currency: currency.currency,
      allocated_amount: Number(amount).toFixed(2),
      is_active: editingIndex === null ? 1 : records[editingIndex]?.is_active ? 1 : 0,
    };
    try {
      setSaving(true);
      const response = await axios.post(
        API_ENDPOINT.ins_budget_config,
        payload,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      const responseRecord =
        response?.data?.response || response?.data || payload;
      const savedRecord = {
        ...responseRecord,
        department: department.name,
        purchase_type: responseRecord.purchase_type || payload.purchase_type,
      };
      setRecords((current) =>
        editingIndex === null
          ? [savedRecord, ...current]
          : current.map((record, index) =>
              index === editingIndex ? savedRecord : record,
            ),
      );
      await loadBudgets();
      setOpen(false);
      setForm(emptyForm);
      setCurrency({ currency: "", countryName: "" });
      setErrors({});
    } catch (error) {
      setErrors({
        submit:
          error?.response?.data?.response ||
          error?.response?.data?.error ||
          "Unable to save budget. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteBudget = async (record, index) => {
    try {
      await axios.post(
        API_ENDPOINT.delete_budget_config,
        {
          admin_token: adminToken,
          id: record.id,
          revision: record.revision,
        },
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      setRecords((current) =>
        current.filter((_, currentIndex) => currentIndex !== index),
      );
      setDeleteSuccessOpen(true);
    } catch (error) {
      setErrors({
        submit:
          error?.response?.data?.message ||
          "Unable to delete budget. Please try again.",
      });
    }
  };

  const toggleBudgetStatus = async (_, row) => {
    const record = records[row.sourceIndex];
    if (!record) return;
    try {
      await axios.post(
        API_ENDPOINT.ins_budget_config,
        {
          admin_token: adminToken,
          id: record.id,
          revision: record.revision,
          department_id: record.department_id,
          financial_year: record.financial_year,
          expense_type: record.expense_type,
          purchase_type: record.purchase_type,
          currency: record.currency,
          allocated_amount: record.allocated_amount,
        is_active: record.is_active ? 0 : 1,
        },
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      await loadBudgets();
    } catch (error) {
      setErrors({
        submit:
          error?.response?.data?.response ||
          error?.response?.data?.message ||
          "Unable to update budget status. Please try again.",
      });
    }
  };

  const fields = [
    <DSelectInput
      key="department"
      label="Department"
      required
      value={form.department}
      options={departments.map((item) => item.name)}
      onChange={(value) =>
        setForm((current) => ({ ...current, department: value || "" }))
      }
      error={errors.department}
      errorMsg="Department is required"
      size="44%"
    />,
    <DSelectInput
      key="year"
      label="Financial Year"
      required
      value={form.year}
      options={years}
      onChange={(value) =>
        setForm((current) => ({ ...current, year: value || "" }))
      }
      error={errors.year}
      errorMsg="Financial year is required"
      size="44%"
    />,
    <DSelectInput
      key="expense"
      label="Expense Type"
      required
      value={form.expenseType}
      options={["CAPEX", "OPEX"]}
      onChange={(value) =>
        setForm((current) => ({ ...current, expenseType: value || "" }))
      }
      error={errors.expenseType}
      errorMsg="Expense type is required"
      size="44%"
    />,
    <DSelectInput
      key="purchase"
      label="Budget Category"
      required
      value={form.purchaseType}
      options={["Direct", "Indirect"]}
      onChange={(value) =>
        setForm((current) => ({ ...current, purchaseType: value || "" }))
      }
      error={errors.purchaseType}
      errorMsg="Budget category is required"
      size="44%"
    />,
    <div key="currency" className={styles.currency}>
      <SelectCountry
        value={currency}
        setValue={setCurrency}
        label="Currency *"
        width="100%"
        gridMd={12}
        error={errors.currency}
        errorMsg="Currency is required"
        disableClearable={false}
      />
    </div>,
    <DTextInput
      key="amount"
      label="Allocate Amount"
      required
      type="text"
      value={formatAllocatedAmount(form.amount)}
      onChange={(event) =>
        setForm((current) => ({
          ...current,
          amount: event.target.value
            .replace(/,/g, "")
            .replace(/[^0-9.]/g, "")
            .replace(/(\..*)\./g, "$1"),
        }))
      }
      error={errors.amount}
      errorMsg="Enter a valid numeric amount"
      size="100%"
    />,
    errors.submit && (
      <div key="error" className={styles.errorCard}>
        {errors.submit}
      </div>
    ),
  ];

  const headings = [
    { label: "Department", field: "department", sortable: false },
    { label: "Financial Year", field: "financial_year", sortable: false },
    { label: "Expense Type", field: "expense_type", sortable: false },
    { label: "Budget Category", field: "purchase_type", sortable: false },
    { label: "Currency", field: "currency", sortable: false },
    { label: "Allocate Amount", field: "allocated_amount" },
    { label: "Balance Amount", field: "balance_amount", sortable: false },
    { label: "Status", field: "status", additional_data: "toggle", sortable: false },
    { label: "Created Date", field: "created_at" },
  ];
  const tableRecords = records.map((record, sourceIndex) => ({
    ...record,
    financial_year: financialYearLabel(record.financial_year),
    status: Boolean(record.is_active),
    created_at: formatIndianDateTime(record.created_at),
    sourceIndex,
  }));
  const filteredRecords = tableRecords;

  return (
    <div className={itemClasses.users_tab_wrapper}>
      <div className={itemClasses.users_topHeader}>
        <div className={`${itemClasses.lhs} ${itemClasses.flex}`}>
          <HeaderTitle
            titleText="Budget Master"
            customclass={itemClasses.dashHead}
          />
        </div>
      </div>
      <div className={itemClasses.search_add_wrapper}>
        <SearchBar
          value={searchQuery}
          onChange={(value) => {
            setSearchQuery(value);
            setPageNumber(1);
          }}
          placeholder="Search budget records"
          tooltip="Search budget records"
        />
        <div className={itemClasses.flex}>
          <DButtonOutlined
            label="UPLOAD"
            btnHeight="4rem"
            onClick={openBulkBudget}
          />
          <DButton
            labelImg={plusIcon}
            iconWidth="1rem"
            iconHeight="1rem"
            label="ADD BUDGET"
            btnHeight="4rem"
            onClick={openAddBudget}
          />
        </div>
      </div>
      <div className={itemClasses.d_dataTable}>
        <DataTable
          headings={headings}
          tableData={filteredRecords}
          actions={true}
          tableSize="100%"
          viewBtn={false}
          deleteBtn={true}
          editBtn={true}
          sorting={true}
          stickyFirstColumn={true}
          isLoading={tableLoading}
          editClick={(id, row) =>
            editBudget(records[row.sourceIndex], row.sourceIndex)
          }
          deleteClick={(id, row) =>
            deleteBudget(records[row.sourceIndex], row.sourceIndex)
          }
          toggleOnChange={toggleBudgetStatus}
          setSortingColumnName={setSortingColumnName}
          sortingColumnName={sortingColumnName}
          setSortingOrder={setSortingOrder}
          sortingOrder={sortingOrder}
          itemsTable={true}
        />
      </div>
      <div className={styles.paginationRight}>
        <PaginationComponent
          currentPage={pageNumber}
          itemsPerPage={paginationData.count}
          totalItems={Math.ceil(
            paginationData.count / paginationData.page_size,
          )}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          disablePrevious={Number(pageNumber) <= 1}
          disableNext={
            Number(pageNumber) >=
            Math.ceil(paginationData.count / paginationData.page_size)
          }
          previouPageClick={() => previouPageClick(pageNumber, setPageNumber)}
          nextPageClick={() => setPageNumber((current) => Number(current) + 1)}
        />
      </div>
      <Modal open={open} onClose={closeModal} closeAfterTransition>
        <Fade in={open}>
          <div className={styles.modal}>
            <PopupFrame
              poupHeading={
                <HeaderTitle
                  titleText={
                    editingIndex === null ? "Add Budget" : "Edit Budget"
                  }
                />
              }
              inputChildren={fields}
              closePopup={closeModal}
              actionButtons={[
                <DButtonOutlined
                  key="cancel"
                  label="Cancel"
                  btnWidth="17rem"
                  btnHeight="4rem"
                  onClick={closeModal}
                />,
                <DButton
                  key="save"
                  label="SAVE"
                  btnWidth="17rem"
                  btnHeight="4rem"
                  onClick={saveBudget}
                  loadingState={saving}
                />,
              ]}
            />
          </div>
        </Fade>
      </Modal>
      <Modal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        closeAfterTransition
      >
        <Fade in={bulkOpen}>
          <div className={styles.modal}>
            <PopupFrame
              poupHeading={<HeaderTitle titleText="Add Bulk Budget" />}
              inputChildren={[
                <div key="upload" className={styles.bulkUpload}>
                  <DefaultLabel label="Upload file" />
                  <DFileUpload
                    fileTypes=".xlsx,.csv"
                    customFileTypeText="(Supported format CSV, XLSX)"
                    onFileUpload={setBulkFile}
                    size="100%"
                  />
                  <p
                    className={styles.templateLink}
                    onClick={downloadBudgetTemplate}
                  >
                  Sample Budget Master
                  </p>
                </div>,
              ]}
              closePopup={() => setBulkOpen(false)}
              actionButtons={[
                <DButtonOutlined
                  key="cancel"
                  label="Cancel"
                  btnWidth="17rem"
                  btnHeight="4rem"
                  onClick={() => setBulkOpen(false)}
                />,
                <DButton
                  key="upload"
                  label="UPLOAD"
                  btnWidth="17rem"
                  btnHeight="4rem"
                  disabled={!bulkFile}
                  loadingState={bulkSaving}
                  error={Boolean(bulkError)}
                  errorMsg={bulkError}
                  onClick={uploadBulkBudget}
                />,
              ]}
            />
          </div>
        </Fade>
      </Modal>
      <Modal
        open={deleteSuccessOpen}
        onClose={() => setDeleteSuccessOpen(false)}
        closeAfterTransition
      >
        <Fade in={deleteSuccessOpen}>
          <div className={styles.modal}>
            <SuccessPopupDashboard
              popupImage={successPopupImg}
              popupHeroText="Budget Deleted Successfully!"
              popupDescription="Budget deleted successfully."
              popBtn={true}
              buyerBtn={true}
              btnText="Okay"
              btnClick={() => setDeleteSuccessOpen(false)}
            />
          </div>
        </Fade>
      </Modal>
    </div>
  );
};

export default BudgetMaster;
